import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import { Text } from '@wix/design-system';
import { Image } from '@wix/wix-ui-icons-common';
import styles from './signature-drawer.css';
import { dataUrlToWixFile, decodeDataUrl, encodeDataUrl } from './utils/data-url';
import { writeTextOnCanvas } from './utils/text';
import { resizeCanvas } from './utils/canvas';
import { focusOnPrevElement } from './utils/focus';
import classNames from 'classnames';
var SignatureFormat = /*#__PURE__*/function (SignatureFormat) {
  SignatureFormat["TEXT"] = "text/plain";
  SignatureFormat["SVG"] = "image/svg+xml";
  return SignatureFormat;
}(SignatureFormat || {});
export const SignatureDrawer = props => {
  const {
    className,
    options,
    onChange: _onChange,
    value,
    theme,
    ...rest
  } = props;
  const pad = React.useRef();
  const inputRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const valueUrl = value == null ? void 0 : value.url;
  const {
    type,
    value: signatureText
  } = decodeDataUrl(valueUrl) ?? {};
  const drawable = !valueUrl || type === SignatureFormat.SVG;
  const writable = drawable || type === SignatureFormat.TEXT;
  const externalImage = !drawable && !writable;
  const getSignature = useCallback(() => {
    var _pad$current, _pad$current3;
    if (type === SignatureFormat.TEXT) {
      return valueUrl ?? '';
    }
    if ((_pad$current = pad.current) != null && _pad$current.isEmpty()) {
      return '';
    }
    try {
      var _pad$current2;
      return ((_pad$current2 = pad.current) == null ? void 0 : _pad$current2.toDataURL(SignatureFormat.SVG)) ?? '';
    } catch {}
    return ((_pad$current3 = pad.current) == null ? void 0 : _pad$current3.toDataURL()) ?? '';
  }, [type, valueUrl]);
  const onChange = useCallback(() => {
    _onChange == null || _onChange(dataUrlToWixFile(getSignature()));
  }, [getSignature, _onChange]);
  const writeOnCanvas = useCallback(newText => {
    var _pad$current4;
    (_pad$current4 = pad.current) == null || _pad$current4.clear();
    if (!newText || !canvasRef.current) {
      return;
    }
    writeTextOnCanvas(canvasRef.current, newText, options == null ? void 0 : options.penColor, options == null ? void 0 : options.backgroundColor);
  }, [options == null ? void 0 : options.backgroundColor, options == null ? void 0 : options.penColor]);
  const drawImageOnPad = useCallback(async dataUrl => {
    if (getSignature() !== dataUrl) {
      const decodedDataUrl = decodeDataUrl(dataUrl);
      if ((decodedDataUrl == null ? void 0 : decodedDataUrl.type) === SignatureFormat.SVG) {
        var _pad$current5;
        (_pad$current5 = pad.current) == null || _pad$current5.fromDataURL(dataUrl);
        const dataUrlAfterResizing = getSignature();
        const didSignatureResize = dataUrlAfterResizing !== dataUrl;
        if (didSignatureResize) {
          onChange();
        }
      }
    }
  }, [getSignature, onChange]);
  const initCanvas = useCallback(() => {
    resizeCanvas(canvasRef.current);
    if (type === SignatureFormat.TEXT) {
      writeOnCanvas(signatureText ?? '');
    } else if (valueUrl) {
      drawImageOnPad(valueUrl);
    } else {
      var _pad$current6;
      (_pad$current6 = pad.current) == null || _pad$current6.clear();
    }
  }, [drawImageOnPad, signatureText, type, valueUrl, writeOnCanvas]);
  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    pad.current = new SignaturePad(canvasRef.current, options);
  }, [options]);
  useEffect(() => {
    if (!canvasRef.current || !pad.current) {
      return;
    }
    initCanvas();
    pad.current.addEventListener('endStroke', onChange);
    window.addEventListener('resize', initCanvas);
    return function () {
      var _pad$current7;
      window.removeEventListener('resize', initCanvas);
      (_pad$current7 = pad.current) == null || _pad$current7.removeEventListener('endStroke', onChange);
    };
  }, [onChange, initCanvas]);
  useEffect(() => {
    if (drawable) {
      var _pad$current8;
      (_pad$current8 = pad.current) == null || _pad$current8.on();
    } else {
      var _pad$current9;
      (_pad$current9 = pad.current) == null || _pad$current9.off();
    }
  }, [drawable]);
  const handleTextChange = useCallback(event => {
    if (writable) {
      const text = event.target.value;
      writeOnCanvas(text);
      if (text.trim()) {
        _onChange == null || _onChange(dataUrlToWixFile(encodeDataUrl(event.target.value, SignatureFormat.TEXT)));
      } else {
        _onChange == null || _onChange(null);
      }
    }
  }, [writable, _onChange, writeOnCanvas]);
  const focusInput = useCallback(ev => {
    if (ev.relatedTarget !== inputRef.current) {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) == null || _inputRef$current.focus();
    } else {
      // When you shift-tab the focus changes from the hidden input to the canvas
      // the user cannot notice a difference because it's the same focus-ring, so it feels like a bug
      // we skip the canvas and focus directly on the element before it
      focusOnPrevElement();
    }
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    "data-theme": theme
  }, /*#__PURE__*/React.createElement("canvas", _extends({}, rest, {
    tabIndex: 0,
    ref: canvasRef,
    onFocus: focusInput,
    onClick: focusInput,
    className: classNames(styles.canvas, {
      [styles.hidden]: externalImage
    })
  })), /*#__PURE__*/React.createElement("input", {
    type: "text",
    tabIndex: -1,
    ref: inputRef,
    value: type === SignatureFormat.TEXT ? signatureText : '',
    onChange: handleTextChange,
    className: styles.hiddenInput
  }), externalImage && /*#__PURE__*/React.createElement("div", {
    className: styles.externalImageOverlay
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.externalImage
  }, /*#__PURE__*/React.createElement(Image, null), /*#__PURE__*/React.createElement(Text, {
    size: "medium",
    weight: "normal"
  }, value == null ? void 0 : value.displayName))));
};
//# sourceMappingURL=signature-drawer.js.map